/**
 * The pounce aim: where this charge actually lands, drawn before it is spent.
 *
 * The charge mechanic in player.js is a promise about two numbers -- apex
 * scales to 1.5x, and a steered charge lunges up to 2.6 m/s along `facing` --
 * and until now the player had to learn both by feel, from a bar that says
 * how *full* the charge is and nothing about where it goes.  That is the wrong
 * question.  A cat lining up a leap is not asking "how charged am I", it is
 * asking "do I make that fence".  So this answers the second one directly: a
 * dotted arc out of the crouched cat and a ring on the exact surface the paws
 * will touch, growing as the hold gathers, swinging as the player aims.
 *
 * THE ONE RULE HERE: the preview must never lie.  A targeting line that is
 * approximately right is worse than none at all -- the player stops reading
 * the world and starts trusting the line, and every place the two disagree
 * becomes a leap into a wall.  So this does not model the flight.  It flies
 * it: a second Player, on the same collider grid and the same ground, handed
 * the state of the real one, launched through the real `launch()` and stepped
 * through the real `update()`.  Every metre drawn is a metre the physics
 * actually produced, past the same walls, the same landing assist, the same
 * ground snap.  There is no second copy of the trajectory arithmetic to drift.
 *
 * What it cannot promise is the player's own next input: the arc is flown
 * with the sticks neutral, so it is the leap you get if you release and let
 * it ride.  Air control can still steer the landing a few tens of centimetres
 * either way (AIR_ACCEL against the airborne floor), which is the difference
 * between a promise and a straitjacket, and is the one place the ring is a
 * good guess rather than a guarantee.
 *
 * This file is cut where burrs.js is cut, and for the same reason (§1.2's
 * standing rule): the oracle flies a real Player and is engine, while the
 * beads and the ring that draw its answer are presentation and live at
 * packages/client/src/pounce.js.  The cut is the author's own "---- the
 * drawing ----" line.  BLOCKED_LOSS below and RING_OUTER there must stay
 * equal across it; contracts.mjs pins the pair.
 */
import * as THREE from "three";
import { Player } from "./player.js";

/** The step the arc is flown at: the game's own reference frame time. */
const DT = 1 / 60;
/** 2.5 s of flight.  A full pounce is 0.83 s; the rest is roof-height drops. */
const MAX_STEPS = 150;

/**
 * Metres of refused travel before the arc reads as obstructed.  Kept equal to
 * RING_OUTER below -- see where r.blocked is set.
 */
const BLOCKED_LOSS = 0.235;

/** Neutral input -- see the flight-it-with-sticks-neutral note above. */
const NEUTRAL = Object.freeze({
  forward: 0, back: 0, left: 0, right: 0, sprint: 0, jump: 0,
  sitToggle: 0, stalk: 0, swipe: 0, claw: 0,
});

/**
 * Flies a pounce forward and reports where it goes.
 *
 * One ghost Player, allocated once and reused every frame: constructing one
 * per prediction would allocate a Box3, three Vector3s and a 33-entry state
 * block sixty times a second, all of it garbage by the next frame.
 */
export class PounceOracle {
  /** @param {any} grid @param {any} groundY -- the level's surfaceY; typed loose
      because player.js's own @param for it is narrower than what the level
      actually hands over, and correcting that is player.js's business */
  constructor(grid, groundY) {
    this.ghost = new Player(grid, groundY);
    // The ghost is a physics puppet, never a cat: no burr snare (it must not
    // consult the real bushes, and a tangle mid-prediction would end the
    // flight in the wrong place), and nothing it does is drawn or networked.
    this.ghost.burrs = null;
    this.path = [];
    for (let i = 0; i <= MAX_STEPS; i++) this.path.push(new THREE.Vector3());
    this.result = { path: this.path, steps: 0, land: new THREE.Vector3(),
                    landed: false, blocked: false, lost: 0, apex: 0,
                    range: 0, air: 0 };
  }

  /**
   * Where `player` lands if it releases the charge this frame.
   *
   * Reads the live player and writes only the ghost, so it is safe to call
   * from anywhere in the frame.  The returned object is reused between calls.
   */
  /** @param {any} player */
  predict(player, power = player.power, aimed = player.aiming) {
    const g = this.ghost;
    const r = this.result;

    // Everything launch() and the flight will read.  Deliberately explicit
    // rather than a loop over Object.keys: the ghost must start each
    // prediction from a KNOWN state, and a field silently added to Player
    // later should show up here as a missing copy, not as a stale value
    // quietly steering last frame's leftovers into this frame's arc.
    g.pos.copy(player.pos);
    g.vel.copy(player.vel);
    g.facing = player.facing;
    g.shapeYaw = player.shapeYaw;
    g.grounded = player.grounded;
    g.groundedFor = player.groundedFor;
    g.airborneFor = player.airborneFor;
    g.clinging = false;
    g.clingBox = null;
    g.wallBox = null;
    g.snagFor = 0;
    g.snagSafe = 0;
    g.snagHeld = false;
    g.sitting = false;
    g.stalking = false;
    g.swipeTimer = 0;
    g.landTimer = 0;
    g.jumpHeld = 0;
    g.charging = false;
    g.chargeTime = 0;
    g.releaseBuffer = 0;
    g.state = "idle";
    g.stateTime = 0;

    // The real launch, off the real player's velocity: this is the frame the
    // preview is promising, so it had better be the same function that runs
    // when the key comes up.
    g.launch(power, aimed);

    const start = this.path[0].copy(g.pos);
    let n = 0, apex = g.pos.y, lost = 0, landed = false;
    for (let i = 0; i < MAX_STEPS; i++) {
      // The horizontal metre this step ASKED for, before the world gets a
      // say.  Sticks are neutral and the cat is airborne, so nothing but a
      // collision can change vel between here and the move.
      const wantX = g.vel.x * DT, wantZ = g.vel.z * DT;
      const px = g.pos.x, pz = g.pos.z;
      g.update(DT, NEUTRAL, 0);
      this.path[++n].copy(g.pos);
      if (g.pos.y > apex) apex = g.pos.y;
      // Metres of intended travel the world refused, summed over the flight.
      //
      // Deliberately a running total rather than "did the capsule touch
      // anything on any frame", which was the first cut and read as
      // obstructed on more than half of all headings out of the spawn: a
      // pounce that clips a kerb or grazes a gatepost on the way through is
      // a good pounce, and an arc that cries wolf on every one of them
      // teaches the player to ignore the colour.  Summed, a graze is the
      // couple of centimetres it actually costs and a wall is the whole
      // remaining leap.
      const want = Math.hypot(wantX, wantZ);
      if (want > 1e-4) lost += want - Math.hypot(g.pos.x - px, g.pos.z - pz);
      if (g.grounded) { landed = true; break; }
    }

    r.steps = n;
    r.landed = landed;
    r.lost = lost;
    // The threshold is the landing ring's own outer radius: a deflection the
    // ring itself would cover is a deflection nobody can see, and colouring
    // it would be the arc reporting a difference it cannot draw.  Past that
    // the leap is visibly not going where it was pointed, which is the one
    // thing this colour is for.
    r.blocked = lost > BLOCKED_LOSS;
    r.apex = apex - start.y;
    r.air = n * DT;
    r.land.copy(this.path[n]);
    r.range = Math.hypot(r.land.x - start.x, r.land.z - start.z);
    return r;
  }
}
